import React, { useEffect, useState } from 'react';
import { adminApi } from '../services/api';
import { ShieldCheck, ShieldOff, Smartphone, KeyRound, RefreshCw, Copy, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Multi-factor authentication panel for the signed-in admin, opened from
 * Settings -> Admin Users. Two methods:
 *   - Authenticator app (TOTP): /mfa/setup gives a key, /mfa/verify confirms.
 *   - SMS codes: /mfa/sms/setup texts a code to the mobile, /mfa/sms/verify
 *     confirms. Needs the platform SMS sender under Settings -> Notifications.
 * Either can be turned off with the account password (/mfa/disable).
 */
const AdminMfaModal = ({ user, onClose, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(null); // null | 'totp' | 'sms' | 'off'
  const [totpSetup, setTotpSetup] = useState(null);
  const [code, setCode] = useState('');
  const [mobile, setMobile] = useState(user?.mobile || '');
  const [smsHint, setSmsHint] = useState('');
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.mfaStatus();
      setStatus(r.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load MFA status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fail = (err, fallback) => setError(err.response?.data?.error || fallback);

  const startTotp = async () => {
    setBusy(true); setError('');
    try {
      const r = await adminApi.mfaSetup();
      setTotpSetup({ secret: r.data.secret, uri: r.data.otpAuthUri });
      setMode('totp');
      setCode('');
    } catch (err) { fail(err, 'Could not start authenticator setup'); } finally { setBusy(false); }
  };

  const verifyTotp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6-digit code from the app.'); return; }
    setBusy(true); setError('');
    try {
      await adminApi.mfaVerify(code);
      toast.success('Authenticator app enabled. You will be asked for a code at sign-in.');
      onChanged();
    } catch (err) { fail(err, 'Code not accepted'); } finally { setBusy(false); }
  };

  const sendSms = async () => {
    setBusy(true); setError('');
    try {
      const r = await adminApi.mfaSmsSetup(mobile);
      setSmsHint(r.data?.maskedMobile || '');
      setMode('sms');
      setCode('');
      toast.success(r.data?.message || 'Code sent');
    } catch (err) { fail(err, 'Could not send the code'); } finally { setBusy(false); }
  };

  const verifySms = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6-digit code from the text message.'); return; }
    setBusy(true); setError('');
    try {
      await adminApi.mfaSmsVerify(code);
      toast.success('SMS codes enabled. You will be texted a code at sign-in.');
      onChanged();
    } catch (err) { fail(err, 'Code not accepted'); } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    if (!password) { setError('Enter your password to turn MFA off.'); return; }
    setBusy(true); setError('');
    try {
      await adminApi.mfaDisable(password);
      toast.success('MFA turned off');
      onChanged();
    } catch (err) { fail(err, 'Could not turn MFA off'); } finally { setBusy(false); }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(totpSetup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable */ }
  };

  const grouped = totpSetup ? totpSetup.secret.replace(/(.{4})/g, '$1 ').trim() : '';
  const codeInput = (placeholder) => (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      maxLength={6}
      required
      autoFocus
      value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      className="input tracking-widest w-40"
      placeholder={placeholder}
    />
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary-600" />
              Multi-factor authentication
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {status && (
                <div className={`rounded-lg border p-3 text-sm ${status.enabled ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                  {status.enabled
                    ? <>MFA is <strong>on</strong> using {status.method === 'sms' ? <>SMS codes to <span className="font-mono">{status.mobile}</span></> : 'an authenticator app'}.</>
                    : <>MFA is <strong>off</strong>. Sign-in only needs your password. Turn it on below; it is required for the encryption key export.</>}
                </div>
              )}

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
              )}

              {mode === null && (
                <div className="space-y-3">
                  <button type="button" onClick={startTotp} disabled={busy || status?.authenticatorAvailable === false} className="w-full text-left rounded-lg border border-gray-200 p-4 hover:border-primary-300 hover:bg-primary-50 disabled:opacity-50">
                    <div className="flex items-start gap-3">
                      <KeyRound className="w-5 h-5 text-primary-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">{status?.method === 'totp' ? 'Replace authenticator app' : 'Use an authenticator app'}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Microsoft Authenticator, Google Authenticator or your password manager. Works without mobile signal.</p>
                        {status?.authenticatorAvailable === false && <p className="text-xs text-red-600 mt-1">Unavailable: ENCRYPTION_KEY is not configured on the API worker.</p>}
                      </div>
                    </div>
                  </button>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start gap-3">
                      <Smartphone className="w-5 h-5 text-primary-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{status?.method === 'sms' ? 'Change SMS mobile' : 'Use SMS codes'}</p>
                        <p className="text-xs text-gray-500 mt-0.5">A 6-digit code is texted to your mobile each time you sign in.</p>
                        {status?.smsAvailable === false && (
                          <p className="text-xs text-amber-700 mt-1">Not available yet: add a Twilio SMS sender number under Settings &rarr; Notifications.</p>
                        )}
                        <div className="flex gap-2 mt-3">
                          <input
                            type="tel"
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                            placeholder="0412 345 678"
                            className="input flex-1"
                          />
                          <button type="button" onClick={sendSms} disabled={busy || !mobile || status?.smsAvailable === false} className="btn-primary disabled:opacity-50">
                            {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Send code
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {status?.enabled && (
                    <button type="button" onClick={() => { setMode('off'); setError(''); }} className="w-full text-left rounded-lg border border-gray-200 p-4 hover:border-red-300 hover:bg-red-50">
                      <div className="flex items-start gap-3">
                        <ShieldOff className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Turn MFA off</p>
                          <p className="text-xs text-gray-500 mt-0.5">Asks for your password. Key export will be unavailable until it is back on.</p>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {mode === 'totp' && totpSetup && (
                <form onSubmit={verifyTotp} className="space-y-4">
                  <div>
                    <p className="font-medium text-gray-900 mb-1">1. Add the account to your authenticator app</p>
                    <p className="text-sm text-gray-600 mb-2">
                      Choose <em>Add account &rarr; Other &rarr; Enter a setup key</em>. Account name: <span className="font-mono">{user?.email}</span>. Key:
                    </p>
                    <div className="flex items-stretch gap-2">
                      <input readOnly value={grouped} onFocus={(e) => e.target.select()} className="input font-mono tracking-wider bg-gray-50" aria-label="Authenticator setup key" />
                      <button type="button" onClick={copySecret} className="btn-secondary px-3">
                        {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Time-based, 6 digits. On a phone you can also open:{' '}
                      <a href={totpSetup.uri} className="text-primary-600 underline break-all">{totpSetup.uri.split('?')[0]}</a>
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-1">2. Enter the code the app shows</p>
                    {codeInput('123456')}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setMode(null); setTotpSetup(null); setCode(''); }} className="btn-secondary">Back</button>
                    <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50">
                      {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                      Verify and enable
                    </button>
                  </div>
                </form>
              )}

              {mode === 'sms' && (
                <form onSubmit={verifySms} className="space-y-4">
                  <p className="text-sm text-gray-700">We texted a 6-digit code to <span className="font-mono">{smsHint || mobile}</span>. It expires in 10 minutes.</p>
                  {codeInput('123456')}
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={sendSms} disabled={busy} className="text-xs text-primary-600 hover:underline disabled:opacity-50">Send a new code</button>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setMode(null); setCode(''); }} className="btn-secondary">Back</button>
                      <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50">
                        {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                        Verify and enable
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {mode === 'off' && (
                <form onSubmit={disable} className="space-y-4">
                  <p className="text-sm text-gray-700">Enter your password to turn multi-factor authentication off.</p>
                  <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="input" placeholder="Your password" />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setMode(null); setPassword(''); }} className="btn-secondary">Back</button>
                    <button type="submit" disabled={busy} className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50">
                      {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-2" />}
                      Turn off
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMfaModal;
